import * as React from 'react';

import { Search } from 'lucide-react';

import type { Template } from '@klareospace/pharos-lib-types';
import { Dialog, DialogContent, DialogHeader, DialogTitle, Input, ScrollArea } from '@klareospace/pharos-lib-web-utils';

import { useCatalogModal } from '../hooks';
import { CatalogGrid } from './catalog-grid';
import { LoadMoreSentinel } from './load-more-sentinel';

// ─── Props ────────────────────────────────────────────────────────────────────

interface CatalogModalProps {
  emptyMessage: string;
  errorMessage: string;
  installLabel: string;
  installedLabel: string;
  isInstalled?: (templateId: string) => boolean;
  onClose: () => void;
  onInstall: (template: Template) => void;
  open: boolean;
  searchAriaLabel: string;
  searchPlaceholder: string;
  subtitle: string;
  title: string;
  type: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CatalogModal({
  emptyMessage,
  errorMessage,
  installLabel,
  installedLabel,
  isInstalled,
  onClose,
  onInstall,
  open,
  searchAriaLabel,
  searchPlaceholder,
  subtitle,
  title,
  type,
}: CatalogModalProps): React.JSX.Element {
  const { error, hasMore, isLoading, isLoadingMore, loadMore, onSearchChange, refetch, searchValue, templates } =
    useCatalogModal(type, open);

  return (
    <Dialog onOpenChange={(isOpen) => !isOpen && onClose()} open={open}>
      <DialogContent className="flex max-h-[90vh] w-full max-w-3xl flex-col gap-0 p-0" dismissOnOutsideClick={false}>
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-label={searchAriaLabel}
              className="pl-9"
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              value={searchValue}
            />
          </div>
        </DialogHeader>
        <ScrollArea className="flex-1 overflow-y-auto">
          <div className="p-6">
            <CatalogGrid
              emptyMessage={emptyMessage}
              error={error}
              errorMessage={errorMessage}
              installLabel={installLabel}
              installedLabel={installedLabel}
              isInstalled={isInstalled}
              isLoading={isLoading}
              onInstall={onInstall}
              onRetry={refetch}
              templates={templates}
            />
            {!isLoading && !error && templates.length > 0 ? (
              <LoadMoreSentinel hasMore={hasMore} isLoadingMore={isLoadingMore} onLoadMore={loadMore} />
            ) : null}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
